import type { StatTestType } from './test-library-datasets.model';

// ---------------------------------------------------------------------------
// Educational tooltips for parameters
// ---------------------------------------------------------------------------

export const PARAM_TOOLTIPS = {
  h0: 'The null hypothesis \u2014 the default assumption we try to disprove with sample data.',
  h1: 'The alternative hypothesis \u2014 what we conclude if the evidence against H\u2080 is strong enough.',
  alpha:
    'The maximum probability of incorrectly rejecting H\u2080 (Type I error). At \u03B1 = 0.05, there is a 1-in-20 chance of a false positive. Lower \u03B1 requires stronger evidence.',
  tailType:
    'Left-tailed: tests if the value is significantly less than expected. Right-tailed: tests if significantly greater. Two-tailed: tests for any significant difference in either direction.',
  nullMean:
    'The hypothesized population mean (\u03BC\u2080) under H\u2080. Your sample mean is compared against this value to judge statistical significance.',
} as const;

// ---------------------------------------------------------------------------
// Visible parameter descriptions (shown below each config control)
// ---------------------------------------------------------------------------

export const PARAM_DESCRIPTIONS = {
  alpha:
    'The maximum probability of incorrectly rejecting H\u2080 (Type I error). Lower \u03B1 requires stronger evidence.',
  tailType: 'Two-tailed (\u2260): unequal. Left-tailed (<): smaller. Right-tailed (>): greater.',
  nullMean:
    'The hypothesized population mean (\u03BC\u2080) under H\u2080. Your sample mean is compared against this value.',
} as const;

// ---------------------------------------------------------------------------
// Contextual test explanation (expandable "How does this test work?")
// ---------------------------------------------------------------------------

export function getTestExplanation(testType: StatTestType): string {
  switch (testType) {
    case 'one-sample-t':
      return (
        'We assume the population mean equals $\\mu_0$ \u2014 this is $H_0$. ' +
        'The **$t$-statistic** measures how far your sample mean $\\bar{x}$ is from $\\mu_0$, ' +
        'scaled by the standard error $s / \\sqrt{n}$. ' +
        'If $t$ is extreme enough \u2014 meaning the $p$-value falls below $\\alpha$ \u2014 we **reject $H_0$** ' +
        'and conclude the true mean differs significantly from $\\mu_0$.'
      );
    case 'two-sample-t':
      return (
        'We assume both groups share the same population mean \u2014 this is $H_0$. ' +
        'The **$t$-statistic** measures the difference between the two sample means $\\bar{x}_1 - \\bar{x}_2$, ' +
        'adjusted for variability and sample sizes. ' +
        'If this difference is large enough ($p$-value $< \\alpha$), we **reject $H_0$** ' +
        'and conclude the group means are significantly different.'
      );
    case 'paired-t':
      return (
        'For each pair, we compute the difference $d_i = x_{\\text{after}} - x_{\\text{before}}$. ' +
        'Under $H_0$, the mean difference $\\bar{d}$ is zero. ' +
        'We run a one-sample $t$-test on these differences \u2014 if $\\bar{d}$ is far enough from zero ' +
        '($p$-value $< \\alpha$), we **reject $H_0$** and conclude there is a significant effect.'
      );
    case 'chi-squared-independence':
      return (
        'For each cell, we compute the **expected frequency** assuming independence: ' +
        '$E = \\frac{\\text{row total} \\times \\text{col total}}{\\text{grand total}}$. ' +
        'The $\\chi^2$ statistic sums how much observed counts deviate from expected. ' +
        'A large $\\chi^2$ ($p$-value $< \\alpha$) means the variables are likely **associated**, not independent.'
      );
    case 'chi-squared-gof':
      return (
        'We compare observed category counts against an expected distribution. ' +
        'The $\\chi^2$ statistic measures the total discrepancy: $\\sum (O_i - E_i)^2 / E_i$. ' +
        "If it's large enough ($p$-value $< \\alpha$), the data **does not follow** the expected distribution."
      );
    case 'sign-test':
      return (
        'We count how many paired differences are positive vs negative (ties are ignored). ' +
        'Under $H_0$, each sign is equally likely \u2014 like a fair coin flip. ' +
        'If one direction dominates significantly, we **reject $H_0$**. ' +
        'No normality assumption is needed, making this a robust non-parametric alternative.'
      );
  }
}

// ---------------------------------------------------------------------------
// Educational descriptions (Markdown with LaTeX, aimed at IB DP students)
// ---------------------------------------------------------------------------

export const TEST_TYPE_DESCRIPTIONS: Readonly<Record<StatTestType, string>> = {
  'one-sample-t':
    '**One-Sample $t$-Test** — Tests whether the mean of a single sample differs significantly from a hypothesized population mean $\\mu_0$.\n\n' +
    '$H_0\\!: \\mu = \\mu_0$ &nbsp;&nbsp; vs &nbsp;&nbsp; $H_1\\!: \\mu \\neq \\mu_0$\n\n' +
    'The test statistic is $t = \\dfrac{\\bar{x} - \\mu_0}{s / \\sqrt{n}}$ with $n - 1$ degrees of freedom.',

  'two-sample-t':
    '**Two-Sample $t$-Test** — Tests whether the means of two independent groups are significantly different.\n\n' +
    '$H_0\\!: \\mu_1 = \\mu_2$ &nbsp;&nbsp; vs &nbsp;&nbsp; $H_1\\!: \\mu_1 \\neq \\mu_2$\n\n' +
    'Assumes both populations are approximately normal. Use the pooled (equal variance) or Welch (unequal variance) version depending on your data.',

  'paired-t':
    '**Paired $t$-Test** — Tests whether the mean difference between paired observations (e.g. before/after) is zero.\n\n' +
    '$H_0\\!: \\mu_d = 0$ &nbsp;&nbsp; vs &nbsp;&nbsp; $H_1\\!: \\mu_d \\neq 0$\n\n' +
    'Compute the differences $d_i = x_{\\text{after},i} - x_{\\text{before},i}$, then apply a one-sample $t$-test to the $d_i$ values.',

  'chi-squared-independence':
    '**$\\chi^2$ Test for Independence** — Tests whether two categorical variables are independent, using a contingency table of observed frequencies.\n\n' +
    '$H_0\\!:$ The two variables are independent &nbsp;&nbsp; vs &nbsp;&nbsp; $H_1\\!:$ They are not independent\n\n' +
    'The test statistic is $\\chi^2 = \\displaystyle\\sum \\dfrac{(O - E)^2}{E}$ where $E = \\dfrac{\\text{row total} \\times \\text{column total}}{\\text{grand total}}$.',

  'chi-squared-gof':
    '**$\\chi^2$ Goodness of Fit Test** — Tests whether observed frequencies match an expected distribution (e.g. uniform, given proportions).\n\n' +
    '$H_0\\!:$ The data follows the expected distribution &nbsp;&nbsp; vs &nbsp;&nbsp; $H_1\\!:$ It does not\n\n' +
    'The test statistic is $\\chi^2 = \\displaystyle\\sum \\dfrac{(O_i - E_i)^2}{E_i}$ with $k - 1$ degrees of freedom, where $k$ is the number of categories.',

  'sign-test':
    '**Sign Test** — A non-parametric test for the median difference between paired observations. No normality assumption is needed.\n\n' +
    '$H_0\\!:$ median difference $= 0$ &nbsp;&nbsp; vs &nbsp;&nbsp; $H_1\\!:$ median difference $\\neq 0$\n\n' +
    'Count the number of positive and negative differences (ignoring ties). Under $H_0$, the number of positive signs follows $B(n, 0.5)$.',
};
